import React from 'react';
import {
  Box,
  Card,
  CardContent,
  IconButton,
  Skeleton,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography
} from '@mui/material';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip as ChartTooltip, XAxis, YAxis } from 'recharts';
import { Line } from 'recharts';
import { ChevronLeft, ChevronRight, ShowChart } from '@mui/icons-material';
import type { DashboardTrendResponse, TrendGranularity } from '../../types/dashboardTrend';

interface DashboardTrendChartProps {
  data: DashboardTrendResponse | null;
  loading: boolean;
  granularity: TrendGranularity;
  onGranularityChange: (value: TrendGranularity) => void;
  onPrev: () => void;
  onNext: () => void;
}

const GRANS: TrendGranularity[] = ['day', 'week', 'month', 'year'];

const DashboardTrendChart: React.FC<DashboardTrendChartProps> = ({
  data,
  loading,
  granularity,
  onGranularityChange,
  onPrev,
  onNext
}) => {
  const buckets = data?.buckets ?? [];
  const chartData = buckets.map((bucket) => ({
    label: bucket.label,
    worked: Math.round(bucket.workedHours * 100) / 100,
    target: Math.round((bucket.targetHours ?? 0) * 100) / 100
  }));

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ShowChart color="primary" />
            <Typography variant="h6">Time trend</Typography>
            <Typography variant="body2" color="text.secondary">
              {data?.window.label || ''}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={granularity}
              onChange={(_, value: TrendGranularity | null) => {
                if (value) onGranularityChange(value);
              }}
            >
              {GRANS.map((g) => (
                <ToggleButton key={g} value={g}>
                  {g}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            <Tooltip title="Previous window">
              <span>
                <IconButton
                  size="small"
                  aria-label="Previous window"
                  onClick={onPrev}
                  disabled={loading || !data?.window.canGoPrev}
                >
                  <ChevronLeft />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Next window">
              <span>
                <IconButton
                  size="small"
                  aria-label="Next window"
                  onClick={onNext}
                  disabled={loading || !data?.window.canGoNext}
                >
                  <ChevronRight />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>

        {loading ? (
          <Box sx={{ mt: 2 }}>
            <Skeleton variant="rounded" height={280} />
          </Box>
        ) : (
          <Box sx={{ width: '100%', height: 300, mt: 2 }}>
            <ResponsiveContainer>
              <BarChart data={chartData} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="label" interval={granularity === 'day' || granularity === 'week' ? 2 : 0} />
                <YAxis />
                <ChartTooltip />
                <Legend />
                <Bar dataKey="worked" name="Worked" fill="#1976d2" />
                <Line
                  type="monotone"
                  dataKey="target"
                  name="Target"
                  stroke="#9c27b0"
                  strokeWidth={2}
                  strokeDasharray="6 4"
                  dot={false}
                  connectNulls
                />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default DashboardTrendChart;
